import React from 'react';
import { Link } from 'react-router-dom';
import { Article } from '@/types/article';
import { formatPublishDate } from '@/utils/articleUtils';

interface ArticleCardProps {
  article: Article;
  featured?: boolean;
}

const ArticleCard: React.FC<ArticleCardProps> = ({ article, featured = false }) => {
  const imageUrl = article.featuredImage || '/placeholder.jpg';
  return (
    <article className={`bg-white rounded-xl shadow-md overflow-hidden flex flex-col h-full transition-transform duration-300 hover:shadow-lg hover:-translate-y-1 ${featured ? 'md:flex-row' : ''}`}>
      {imageUrl && (
        <div className={featured ? 'md:w-1/2' : ''}>
          <Link to={`/${article.slug}`}>
            <img 
              src={imageUrl}
              alt={article.title}
              className={`w-full ${featured ? 'h-80' : 'h-48'} object-cover`}
            />
          </Link>
        </div>
      )}
      <div className={`p-6 flex-grow ${featured ? 'md:w-1/2' : ''}`}>
        <div className="flex flex-wrap gap-2 mb-3">
          {article.categories.map((category) => (
            <Link 
              key={category.id}
              to={`/categories/${category.slug}`}
              className="bg-blue-50 text-blue-600 px-2 py-1 rounded-full text-xs font-medium hover:bg-blue-100"
            >
              {category.name}
            </Link>
          ))}
        </div>
        <h3 className={`font-bold mb-2 line-clamp-2 text-gray-900 ${featured ? 'text-2xl' : 'text-xl'}`}>
          <Link to={`/${article.slug}`} className="hover:text-blue-600 transition-colors">
            {article.title}
          </Link>
        </h3>
        <div className="flex items-center text-gray-500 text-sm mb-3">
          <span>{formatPublishDate(article.publishedAt)}</span>
          <span className="mx-2">•</span>
          <span>{article.readTime} min read</span>
        </div>
        <p className="text-gray-600 mb-4 line-clamp-3">{article.excerpt}</p>
        <Link 
          to={`/${article.slug}`}
          className="text-blue-600 hover:text-blue-800 font-medium inline-flex items-center"
        >
          Read Article
          <svg className="ml-1 h-4 w-4" fill="currentColor" viewBox="0 0 20 20" xmlns="http://www.w3.org/2000/svg">
            <path fillRule="evenodd" d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z" clipRule="evenodd" />
          </svg>
        </Link>
      </div>
    </article>
  );
};

export default ArticleCard; 